import { useState, useRef } from "react";

const defaultColors = ["#4A90D9", "#7B61FF", "#E05D5D", "#3CB371", "#FF8C42", "#6C5CE7", "#00B4D8", "#E76F51", "#2A9D8F", "#F4A261", "#264653", "#A855F7"];

export default function EventEditor({ event, events, onSave, onDelete, onClose }) {
  const isNew = !event;
  const [title, setTitle] = useState(event?.title || "");
  const [description, setDescription] = useState(event?.description || "");
  const [type, setType] = useState(event?.type || "range");
  const [startDate, setStartDate] = useState(event?.startDate || "");
  const [endDate, setEndDate] = useState(event?.endDate || "");
  const [color, setColor] = useState(event?.color || defaultColors[0]);
  const [textSize, setTextSize] = useState(event?.textSize || "14px");
  const [parentId, setParentId] = useState(event?.parentId || "");
  const [image, setImage] = useState(event?.image || "");
  const [imagePreview, setImagePreview] = useState(event?.image || "");
  const fileInputRef = useRef(null);

  const handleImageFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      setImage(dataUrl);
      setImagePreview(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImage(url);
    setImagePreview(url);
  };

  const handleClearImage = () => {
    setImage("");
    setImagePreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!title.trim() || !startDate) return;

    const eventData = {
      title: title.trim(),
      type,
      startDate,
      color,
      textSize,
      description: description.trim(),
      image: image || "",
    };

    if (type === "range" && endDate) {
      eventData.endDate = endDate;
    } else {
      eventData.endDate = "";
    }

    eventData.parentId = parentId || "";

    onSave(eventData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{isNew ? "Add Event" : "Edit Event"}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="event-title">Title</label>
            <input id="event-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Event title..." autoFocus />
          </div>

          <div className="form-group">
            <label htmlFor="event-description">Description</label>
            <textarea
              id="event-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>Image (optional)</label>
            <div className="image-input-row">
              <input
                type="text"
                value={image && !image.startsWith("data:") ? image : ""}
                onChange={handleImageUrlChange}
                placeholder="Image URL..."
                className="image-url-input"
              />
              <span className="image-or">or</span>
              <button type="button" className="btn btn-secondary btn-upload" onClick={() => fileInputRef.current?.click()}>
                Upload
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageFile} style={{ display: "none" }} />
              {imagePreview && (
                <button type="button" className="btn btn-secondary btn-clear-img" onClick={handleClearImage} title="Remove image">
                  &times;
                </button>
              )}
            </div>
            {imagePreview && (
              <div className="image-preview">
                <img src={imagePreview} alt="Preview" />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="event-type">Type</label>
            <select id="event-type" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="range">Range (start & end)</option>
              <option value="punctual">Punctual (single date)</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="event-start">Start Date</label>
              <input id="event-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>

            {type === "range" && (
              <div className="form-group">
                <label htmlFor="event-end">End Date</label>
                <input id="event-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-picker">
              {defaultColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-swatch ${color === c ? "selected" : ""}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setColor(c)}
                  title={c}
                />
              ))}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="event-textsize">Text Size: {textSize}</label>
            <input
              id="event-textsize"
              type="range"
              min="10"
              max="24"
              step="1"
              value={parseInt(textSize)}
              onChange={(e) => setTextSize(`${e.target.value}px`)}
            />
          </div>

          <div className="form-group">
            <label htmlFor="event-parent">Parent Event (for bifurcation)</label>
            <select id="event-parent" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">(None — top-level event)</option>
              {events
                .filter((e) => e.id !== event?.id)
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title} ({e.startDate})
                  </option>
                ))}
            </select>
          </div>

          <div className="modal-actions">
            {!isNew && (
              <button type="button" className="btn btn-danger" onClick={() => onDelete(event.id)}>
                Delete
              </button>
            )}
            <div className="modal-actions-right">
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                {isNew ? "Add Event" : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
